import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faHeart, faChevronLeft, faChevronRight, faShare, faLink, faEnvelope, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faInstagram, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { getUserFavorites, getPOIById, getUserTrips, getAmenities } from '../services/api';
import cacheService from '../services/cacheService';
import Modal from './Modal';
import L from 'leaflet';
import Toast from './Toast';
import { AuthContext } from '../contexts/AuthContext';
import TripCreationModal from './TripCreationModal';
import AuthModal from './AuthModal';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Container = styled.div`
  width: ${props => props.isModal ? '100%' : 'auto'};
  height: ${props => props.isModal ? '100%' : 'auto'};
  display: flex;
  flex-direction: column;
  background-color: white;
  position: relative;

  @media (max-width: 768px) {
    height: 100vh;
    overflow-y: auto;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: ${props => props.isModal ? 'row' : 'column'};
  height: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  background-color: #F6F7FB;
  border: none;
  color: ${props => props.active ? '#14464F' : '#8B9DA0'};
  cursor: pointer;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  background-color: ${props => props.active ? '#F6F7FB' : '#F6F7FB'};

  &:hover {
    background-color: ${props => props.active ? '#e8e9ed' : '#e8e9ed'};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  z-index: 1003;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ImageSection = styled.div`
  flex: ${props => props.isModal ? '1' : 'none'};
  position: relative;
  overflow: hidden;
  min-height: 300px;

  @media (max-width: 768px) {
    flex: none;
    height: 30vh; // Adjust this value to control image height on mobile
    min-height: 200px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentIndex * 100}%);
`;

const Image = styled.div`
  flex: 0 0 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  cursor: pointer;
`;

const ImageNavigationButtons = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 1002;
`;

const ImageNavigationButton = styled.button`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;
  font-size: 1.2em;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const InfoSection = styled.div`
  flex: ${props => props.isModal ? '1' : 'none'};
  padding: 20px;
  overflow-y: auto;
  position: relative;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  @media (max-width: 768px) {
    flex: 1;
    padding: 15px;
  }
`;


const Title = styled.h2`
  color: #0A2429;
  text-align: left;
  margin: 0;
  font: normal normal bold 30px/40px Poppins;
  letter-spacing: 0px;
  font-style: normal;
`;

const Description = styled.p`
  color: #0A2429;
  text-align: left;
  font: normal normal medium 16px/25px Poppins;
  letter-spacing: 0px;
  margin: 0;
  text-justify: inter-word;
  hyphens: auto;
  position: relative;
  margin-bottom: 10px;
  white-space: pre-line;
`;


const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  h3 {
    margin: 0;
  }
`;

const MapMiniature = styled(MapContainer)`
  height: 200px;
  width: 100%;
  margin-bottom: 10px;
  pointer-events: none; // Disable all pointer events
`;

const SectionCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px; // Changed from 20px to 10px
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  height: 90vh;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }
`;

const PlacesSection = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding-bottom: 5px;
  margin-top: 15px;
  
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.5);
    border-radius: 20px;
  }
`;

const PlaceCard = styled.div`
  flex: 0 0 190px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }
`;

const PlaceImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
`;

const PlaceInfo = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  position: relative;
  background-color: #F6F7FB;
`;

const PlaceTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  text-align: left;
  color: #333;
`;

const Address = styled.div`
  font-size: 12px;
  color: #666;
`;

const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #14464F;
  font-size: 16px;
  flex-shrink: 0;
`;

const BookButton = styled.div`
  padding: 10px;
  color: #F38530;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background-color: #F6F7FB;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 120px;
  background-color: #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
  font-size: 14px;
`;

const AddToTripButton = styled.button`
  padding: 10px 20px;
  background-color: #F38530;
  color: #FFFFFF;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 80%;
  text-align: center;
  text-transform: uppercase;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0px;  
  &:hover {
    background-color: #d67428;
  }
`;


const PlaceAddToTripButton = styled(AddToTripButton)`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.9em;
  margin-top: 10px;
`;

const DropdownMenu = styled.div`
  position: ${props => props.isModal ? 'absolute' : 'fixed'};
  ${props => props.isModal ? `
    bottom: 90px;
    right: 20px;
  ` : `
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 1002;
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
`;

const DropdownItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  cursor: pointer;
  
  span.trip-name {
    font-weight: 500;
  }
  
  span.trip-dates {
    font-size: 12px;
    color: #666;
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

const NewTripOption = styled(DropdownItem)`
  background-color: #f8f8f8;
  border-bottom: 2px solid #e0e0e0;
  color: #316C6E;
  font-weight: 500;
  padding: 12px 15px;
  
  &:hover {
    background-color: #eef8f8;
  }
`;


const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 10px;
  cursor: pointer;
  z-index: 10;
`;


const AmenityList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  margin-top: 10px;
  overflow: hidden;
  position: relative;
  height: ${props => props.expanded ? 'auto' : '32px'};
  flex-wrap: ${props => props.expanded ? 'wrap' : 'nowrap'};
`;

const AmenityTag = styled.span`
  background-color: #f0f0f0;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ExpandAmenitiesButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 12px;
  cursor: pointer;
  padding: 5px 10px;
  margin-left: 5px;
  position: absolute;
  right: 0;
  top: 0;
  height: 32px;
  background: linear-gradient(90deg, transparent, #fff 30%);
  padding-left: 30px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SharePopup = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 10px;
  display: flex;
  gap: 10px;
  z-index: 1004;
  margin-top: 5px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  ${props => props.isVisible && `
    opacity: 1;
    visibility: visible;
  `}
`;

const transformHotelData = (hotel) => ({
  id: hotel.hotelId,
  name: hotel.name,
  description: 'Hotel near point of interest',
  image_url: hotel.media?.[0]?.uri || 'https://via.placeholder.com/300x200?text=Hotel+Image',
  type: 'hotel',
  lat: parseFloat(hotel.latitude),
  lng: parseFloat(hotel.longitude),
  hotelId: hotel.hotelId,
  isAmadeusHotel: true
});

const ShareButton = styled(ActionButton)`
  position: relative;
  color: #14464F;
  background-color: #F6F7FB;

  &:hover {
    background-color: #e8e9ed;
  }

  &:hover ${SharePopup} {
    opacity: 1;
    visibility: visible;
  }
`;

const SocialIcon = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 1.2rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &.whatsapp {
    background-color: #25D366;
  }

  &.twitter {
    background-color: #000000;
  }

  &.email {
    background-color: #4285F4;
  }

  &.copy {
    background-color: #6c757d;
  }
`;

const MobileCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 2;
  cursor: pointer;

  @media (max-width: 768px) {
    display: flex;
  }
`;


const TripOption = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  span.trip-name {
    font-weight: 500;
  }
  
  span.trip-dates {
    font-size: 12px;
    color: #666;
  }
`;

const SkeletonCard = styled.div`
  flex: 0 0 200px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 120px;
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonText = styled.div`
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '80%'};
  margin: ${props => props.margin || '10px auto'};
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0;
  padding: 0 20px;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 14px;
  font-weight: 500;
  padding: 5px 0;
  cursor: pointer;
  display: block;
  margin-top: 5px;
  margin-left: 0;
  text-align: left;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1004;
  font-size: 18px;
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
  }
`;

const ModalImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
`;

const ModalImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  opacity: 0;
  transform: scale(0.98);
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

  &.loaded {
    opacity: 1;
    transform: scale(1);
  }
`;

const ModalNavigationButton = styled(ImageNavigationButton)`
  z-index: 1200;
  padding: 15px;
  font-size: 1.2em;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

// Add this helper function at the component level (before the POIDetails component)
const calculateTextHeight = (text, containerWidth, lineHeight = 25, fontSize = 16) => {
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `
    font: normal normal medium ${fontSize}px/25px Poppins;
    width: ${containerWidth}px;
    position: absolute;
    visibility: hidden;
    white-space: normal;
    padding: 0;
  `;
  tempDiv.innerText = text;
  document.body.appendChild(tempDiv);
  const height = tempDiv.offsetHeight;
  document.body.removeChild(tempDiv);
  return height;
};

const POIDetails = ({ 
  poi: initialPoi, 
  onClose, 
  amenities: initialAmenities, 
  isModal = true, 
  onFavoriteRemoved,
  onShowToast 
}) => {
  const [poi, setPoi] = useState(initialPoi);
  const [showMore, setShowMore] = useState(false);
  const [placeToStayOpen, setPlaceToStayOpen] = useState(false);
  const [placeToEatOpen, setPlaceToEatOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [placeToStay, setPlaceToStay] = useState(null);
  const [placeToEat, setPlaceToEat] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const contentRef = useRef();
  const { isAuthenticated, login } = useContext(AuthContext);
  const [howToGet, setHowToGet] = useState('');
  const [placesToStay, setPlacesToStay] = useState([]);
  const [placesToEat, setPlacesToEat] = useState([]);
  const [placesToStayOpen, setPlacesToStayOpen] = useState(false);
  const [placesToEatOpen, setPlacesToEatOpen] = useState(false);
  const [userTrips, setUserTrips] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSavedPOIs, setHasSavedPOIs] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [showTripCreationModal, setShowTripCreationModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const history = useHistory();
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [numPeople, setNumPeople] = useState(1);

  const dropdownRef = useRef(null);
  const [amenities, setAmenities] = useState(initialAmenities || []);
  const [localAmenities, setLocalAmenities] = useState(initialAmenities || []);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [autoMove, setAutoMove] = useState(true);
  const [toastLink, setToastLink] = useState(null);
  const [toastType, setToastType] = useState('success');
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const { language } = useLanguage();
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    if (language === 'es') {
      return item[`${field}_es`] || item[field];
    }
    return item[field];
  };

  const showToastMessage = (message, type = 'success', link = null) => {
    if (onShowToast) {
      onShowToast(message, type, link);
    } else {
      setToastMessage(message);
      setToastType(type);
      setToastLink(link);
      setShowToast(true);
    }
  };

  const fetchUserTrips = async () => {
    if (isAuthenticated) {
      try {
        const trips = await getUserTrips();
        setUserTrips(trips);
      } catch (error) {
        console.error('Error fetching user trips:', error);
        setUserTrips([]);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserTrips();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchPOIDetails = async () => {
      try {
        console.log('Fetching POI details for ID:', initialPoi.id);
        const [poiDetails, amenitiesData] = await Promise.all([
          getPOIById(initialPoi.id),
          initialAmenities ? Promise.resolve(initialAmenities) : getAmenities()
        ]);
        if (!isMounted) return;
        console.log('Fetched POI details:', poiDetails);
        setPoi(poiDetails);
        setHowToGet(poiDetails.howToGet || poiDetails['how-to-get'] || '');

        if (poiDetails.placesToStay && Array.isArray(poiDetails.placesToStay)) {
          setPlacesToStay(poiDetails.placesToStay);
        } else {
          console.log('No places to stay found for this POI');
        }

        if (poiDetails.placesToEat && Array.isArray(poiDetails.placesToEat)) {
          setPlacesToEat(poiDetails.placesToEat);
        } else {
          console.log('No places to eat found for this POI');
        }

        if (!initialAmenities) {
          setAmenities(amenitiesData);
          setLocalAmenities(amenitiesData);
        }

        if (isMounted) {
          const parsedImages = typeof poiDetails.image_url === 'string' && poiDetails.image_url.startsWith('[') 
            ? JSON.parse(poiDetails.image_url) 
            : [poiDetails.image_url];
          setImages(parsedImages);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Error fetching POI details:', error);
      }
    };

    fetchPOIDetails();

    return () => {
      isMounted = false;
    };
  }, [initialPoi.id, initialAmenities]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (isAuthenticated) {
        try {
          const favorites = await getUserFavorites();
          setIsFavorite(favorites.some(fav => fav.id === poi.id));
        } catch (error) {
          console.error('Error fetching favorites:', error);
        }
      } else {
        const localFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        setIsFavorite(localFavorites.includes(poi.id));
      }
    };

    checkFavoriteStatus();
  }, [isAuthenticated, poi.id]);

  useEffect(() => {
    let timer;
    if (autoMove) {
      timer = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000); // Change image every 5 seconds
    }

    return () => clearInterval(timer);
  }, [images, autoMove]);

  useEffect(() => {
    if (poi?.description) {
      const updateHasMoreContent = () => {
        const container = document.querySelector('.info-section');
        if (!container) return;
        
        const containerWidth = container.offsetWidth - 40; // Subtract padding
        const text = language === 'es' ? poi.description_es : poi.description;
        const textHeight = calculateTextHeight(text, containerWidth);
        const lineHeight = 25;
        const threeLineHeight = lineHeight * 3;
        setHasMoreContent(textHeight > threeLineHeight);
      };

      // Initial check
      updateHasMoreContent();

      // Add resize listener
      window.addEventListener('resize', updateHasMoreContent);
      return () => window.removeEventListener('resize', updateHasMoreContent);
    }
  }, [poi, language]);

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (isFavorite) {
      try {
        // If we're in modal view and have onFavoriteRemoved callback, let the parent handle the API call
        if (isModal && onFavoriteRemoved) {
          onFavoriteRemoved();
          setIsFavorite(false);
          return;
        }
        
        // Otherwise handle it here
        await cacheService.removeFavorite(poi.id);
        setIsFavorite(false);
        showToastMessage('Removed from favorites');
      } catch (error) {
        console.error('Error removing favorite:', error);
        showToastMessage('Failed to remove from favorites', 'error');
      }
    } else {
      try {
        await cacheService.addFavorite(poi.id);
        showToastMessage('Successfully added to favorites');
        setIsFavorite(true);
      } catch (error) {
        console.error('Error updating favorite:', error);
        showToastMessage('Error updating favorite. Please try again.', 'error');
      }
    }
  };

  const handleAddToTrip = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (userTrips.length === 0) {
      fetchUserTrips();
    }
    setSelectedPlaceId(poi.id);
    setShowDropdown(true);
  };

  const handleTripSelect = async (tripId) => {
    try {
      await cacheService.addPOIToTrip(tripId, selectedPlaceId);
      setShowDropdown(false);
      
      showToastMessage(translations[language].poi.addToTripSuccess, 'success', {
        url: `/trip/${tripId}`,
        text: 'View Trip'
      });
    } catch (error) {
      if (error.response && error.response.status === 409) {
        showToastMessage(translations[language].poi.alreadyInTrip, 'error');
      } else {
        showToastMessage(translations[language].poi.addToTripError, 'error');
      }
      setShowDropdown(false);
    }
  };

  const handleCreateTrip = async (tripData) => {
    try {
      const newTrip = await cacheService.createTrip({
        name: `Trip to ${poi.name}`,
        ...tripData,
        pois: [selectedPlaceId]
      });
      setShowTripCreationModal(false);
      
      showToastMessage('Trip created successfully!', 'success', {
        url: `/trip/${newTrip.id}`,
        text: 'View Trip'
      });
    } catch (error) {
      console.error('Error creating trip:', error);
      showToastMessage('Failed to create trip. Please try again.', 'error');
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    fetchUserTrips();
  };

  const addPOIToNewTrip = (placeId) => {
    const savedPOIs = JSON.parse(localStorage.getItem('savedPOIs')) || [];
    if (!savedPOIs.includes(placeId)) {
      savedPOIs.push(placeId);
      localStorage.setItem('savedPOIs', JSON.stringify(savedPOIs));
      setToastMessage('Activity added to trip in draft. Start planning your trip now!');
      setShowToast(true);
      setHasSavedPOIs(true);
    } else {
      setToastMessage('This activity is already in your trip in draft.');
      setShowToast(true);
    }
    setShowDropdown(null);
  };

  const addPOIToExistingTrip = async (tripId) => {
    try {
      await cacheService.addPOIToTrip(tripId, selectedPlaceId);
      
      // Add link to view the trip
      const link = {
        url: `/trip/${tripId}`,
        text: '[View Trip]'
      };
      
      showToastMessage(translations[language].poi.addToTripSuccess, 'success', link);
      setShowDropdown(null);
      setIsAdded(true);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        showToastMessage(translations[language].poi.alreadyInTrip, 'error');
      } else {
        showToastMessage(translations[language].poi.addToTripError, 'error');
      }
      setToastLink(null);
      setShowToast(true);
    }
  };

  const handlePrevImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleNextImage = () => {
    setAutoMove(false);
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const confirmAddToTrip = (tripId) => {
    addPOIToExistingTrip(tripId);
    setIsAdded(true); // Set isAdded to true after successful addition
  };

  const renderPlaces = (places, title, isOpen, setIsOpen) => (
    <SectionCard>
      <SectionHeader onClick={() => setIsOpen(!isOpen)}>
        <h3>{title}</h3>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
      </SectionHeader>
      {isOpen && (
        <PlacesSection>
          {places.map((place, index) => {
            const bookingLink = place.link;
            const hasBookingLink = bookingLink && bookingLink !== '#';

            return (
              <PlaceCard key={index}>
                <PlaceImage src={place.image_url} alt={getLocalizedField(place, 'name')} onClick={() => setModalImage(place.image_url)} />
                <PlaceInfo>
                  <PlaceTitle>{getLocalizedField(place, 'name')}</PlaceTitle>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {!isAdded ? (
                      <PlaceAddToTripButton onClick={() => handleAddToTrip(place.id, 'subPlace')}>
                        {translations[language].poi.addToTrip}
                      </PlaceAddToTripButton>
                    ) : hasBookingLink && (
                      <PlaceAddToTripButton onClick={() => window.open(bookingLink, '_blank')}>
                        {translations[language].hotelBooking.bookNow}
                      </PlaceAddToTripButton>
                    )}
                    {hasBookingLink && (
                      <PlaceAddToTripButton onClick={() => window.open(bookingLink, '_blank')}>
                        {translations[language].poi.viewDetails}
                      </PlaceAddToTripButton>
                    )}
                  </div>
                </PlaceInfo>
              </PlaceCard>
            );
          })}
        </PlacesSection>
      )}
    </SectionCard>
  );

  const customIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  const handleSocialShare = (platform, poi) => {
    const baseUrl = window.location.origin;
    const poiUrl = `${baseUrl}/poi/${poi.id}`;
    const shareText = `I'm excited to explore ${poi.name}!`;

    switch (platform) {
      case 'whatsapp':
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const whatsappUrl = isMobile
          ? `whatsapp://send?text=${encodeURIComponent(`${shareText}\n${poiUrl}`)}`
          : `https://web.whatsapp.com/send?text=${encodeURIComponent(`${shareText}\n${poiUrl}`)}`;
        window.open(whatsappUrl, '_blank');
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(poiUrl)}&hashtags=Travel`,
          '_blank'
        );
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(`Check out ${poi.name}!`)}&body=${encodeURIComponent(`${shareText}\n\n${poiUrl}`)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareText}\n${poiUrl}`).then(() => {
          setToastMessage('Link copied to clipboard!');
          setShowToast(true);
        });
        break;
      default:
        break;
    }
  };

  const handleImageClick = (image, index) => {
    setModalImage(image);
    setModalImageIndex(index);
    setImageLoaded(false);
  };

  const handleModalPrevImage = () => {
    const newIndex = (modalImageIndex - 1 + images.length) % images.length;
    setModalImage(images[newIndex]);
    setModalImageIndex(newIndex);
    setImageLoaded(false);
  };

  const handleModalNextImage = () => {
    const newIndex = (modalImageIndex + 1) % images.length;
    setModalImage(images[newIndex]);
    setModalImageIndex(newIndex);
    setImageLoaded(false);
  };

  const content = (
    <Container isModal={isModal}>
      {isModal && <MobileCloseButton onClick={onClose}>×</MobileCloseButton>}
      <Content isModal={isModal}>
        <ImageSection isModal={isModal}>
          <ImageWrapper currentIndex={currentImageIndex}>
            {images.map((image, index) => (
              <Image 
                key={index} 
                image={image} 
                onClick={() => handleImageClick(image, index)}
              />
            ))}
          </ImageWrapper>
          <ActionButtonsContainer>
            <ActionButton
              onClick={handleFavorite}
              active={isFavorite}
              data-tooltip={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <FontAwesomeIcon icon={faHeart} />
            </ActionButton>
            <ShareButton>
              <FontAwesomeIcon icon={faShare} />
              <SharePopup>
                <SocialIcon 
                  className="whatsapp" 
                  onClick={() => handleSocialShare('whatsapp', poi)}
                  title="Share on WhatsApp"
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                </SocialIcon>
                <SocialIcon 
                  className="twitter" 
                  onClick={() => handleSocialShare('twitter', poi)}
                  title="Share on X"
                >
                  <FontAwesomeIcon icon={faXTwitter} />
                </SocialIcon>
                <SocialIcon 
                  className="email" 
                  onClick={() => handleSocialShare('email', poi)}
                  title="Share via Email"
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </SocialIcon>
                <SocialIcon 
                  className="copy" 
                  onClick={() => handleSocialShare('copy', poi)}
                  title="Copy link"
                >
                  <FontAwesomeIcon icon={faLink} />
                </SocialIcon>
              </SharePopup>
            </ShareButton>
          </ActionButtonsContainer>
          {images.length > 1 && (
            <ImageNavigationButtons>
              <ImageNavigationButton onClick={handlePrevImage}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </ImageNavigationButton>
              <ImageNavigationButton onClick={handleNextImage}>
                <FontAwesomeIcon icon={faChevronRight} />
              </ImageNavigationButton>
            </ImageNavigationButtons>
          )}
        </ImageSection>
        <InfoSection isModal={isModal} className="info-section">
          <Title>{getLocalizedField(poi, 'name')}</Title>
          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <Description>
              {language === 'es' ? poi?.description_es : poi?.description}
            </Description>
          </div>
          
          <ButtonsContainer>
            <AddToTripButton onClick={handleAddToTrip}>
              {translations[language].poi.addToTrip}
            </AddToTripButton>
            <ActionButtonsContainer>
              <ActionButton
                onClick={handleFavorite}
                active={isFavorite}
                data-tooltip={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <FontAwesomeIcon icon={faHeart} />
              </ActionButton>
              <ShareButton>
                <FontAwesomeIcon icon={faShare} />
                <SharePopup>
                  <SocialIcon 
                    className="whatsapp" 
                    onClick={() => handleSocialShare('whatsapp', poi)}
                    title="Share on WhatsApp"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </SocialIcon>
                  <SocialIcon 
                    className="twitter" 
                    onClick={() => handleSocialShare('twitter', poi)}
                    title="Share on X"
                  >
                    <FontAwesomeIcon icon={faXTwitter} />
                  </SocialIcon>
                  <SocialIcon 
                    className="email" 
                    onClick={() => handleSocialShare('email', poi)}
                    title="Share via Email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                  </SocialIcon>
                  <SocialIcon 
                    className="copy" 
                    onClick={() => handleSocialShare('copy', poi)}
                    title="Copy link"
                  >
                    <FontAwesomeIcon icon={faLink} />
                  </SocialIcon>
                </SharePopup>
              </ShareButton>
            </ActionButtonsContainer>
          </ButtonsContainer>
          
          {placesToStay.length > 0 && renderPlaces(placesToStay, translations[language].poi.recommendedHotels, placesToStayOpen, setPlacesToStayOpen)}
          {placesToEat.length > 0 && renderPlaces(placesToEat, translations[language].poi.recommendedRestaurants, placesToEatOpen, setPlacesToEatOpen)}
          
          {poi.tips && (
            <SectionCard>
              <SectionHeader onClick={() => setTipsOpen(!tipsOpen)}>
                <h3>{translations[language].poi.tips}</h3>
                <FontAwesomeIcon icon={tipsOpen ? faChevronUp : faChevronDown} />
              </SectionHeader>
              {tipsOpen && <p>{getLocalizedField(poi, 'tips')}</p>}
            </SectionCard>
          )}
        </InfoSection>
        {showDropdown && (
          <DropdownMenu ref={dropdownRef} isModal={isModal}>
            <NewTripOption onClick={() => setShowTripCreationModal(true)}>
              ＋ {translations[language].pages.myTrips.createNew}
            </NewTripOption>
            {userTrips.map(trip => (
              <TripOption key={trip.id} onClick={() => handleTripSelect(trip.id)}>
                <span className="trip-name">{trip.name}</span>
                <span className="trip-dates">
                  {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                </span>
              </TripOption>
            ))}
          </DropdownMenu>
        )}
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
          />
        )}
        {showTripCreationModal && (
          <TripCreationModal
            onClose={() => setShowTripCreationModal(false)}
            onCreateTrip={handleCreateTrip}
            initialPOI={initialPoi}
          />
        )}
      </Content>
      {showToast && (
        <Toast 
          message={toastMessage} 
          onClose={() => {
            setShowToast(false);
            setToastLink(null);
          }} 
          type={toastType}
          link={toastLink}
        />
      )}
      
      {modalImage && (
        <Modal 
          onClose={() => {
            setModalImage(null);
            setModalImageIndex(0);
            setImageLoaded(false);
          }}
          fullScreen
        >
          <CloseModalButton onClick={() => {
            setModalImage(null);
            setModalImageIndex(0);
            setImageLoaded(false);
          }}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseModalButton>
          <ModalImageContainer>
            <ModalImage 
              src={modalImage} 
              alt="Place" 
              className={imageLoaded ? 'loaded' : ''}
              onLoad={() => setImageLoaded(true)}
            />
            {images.length > 1 && (
              <>
                <ModalNavigationButton 
                  direction="left" 
                  onClick={handleModalPrevImage}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </ModalNavigationButton>
                <ModalNavigationButton 
                  direction="right" 
                  onClick={handleModalNextImage}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </ModalNavigationButton>
              </>
            )}
          </ModalImageContainer>
        </Modal>
      )}
      {showTripCreationModal && (
        <TripCreationModal
          onClose={() => setShowTripCreationModal(false)}
          onCreateTrip={handleCreateTrip}
          initialPOI={initialPoi}
        />
      )}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      )}
    </Container>
  );

  if (isModal) {
    return (
      <ModalWrapper onClick={onClose}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          {content}
        </ModalContainer>
      </ModalWrapper>
    );
  }

  return content;
};

export default POIDetails;
